// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { FloatingTooltipStyles } from "@steiner/App/Components/FloatingTooltip/FloatingTooltip";
import HeaderClickable, { HeaderClickableStyles } from "@steiner/App/Components/Header/HeaderClickable";
import FloatingMenuTooltip, {
    DEFAULT_STEINER_OFFSET,
    DEFAULT_STEINER_STICKY_OFFSET,
} from "@steiner/App/Components/Menu/FloatingMenuTooltip";
import MiniContactUs from "@steiner/App/Components/MiniContactUs/MiniContactUs";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import { getSettingsCollection } from "@steiner/App/Store/Selectors/SettingsSelectors";
import { XNGAGE_ELEMENTS } from "@steiner/XDL/constants";
import React, { FC, useState } from "react";
import { shallowEqual } from "react-redux";

const enum fields {
    isCompactHeader = "isCompactHeader",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.isCompactHeader]: boolean;
    };
}

type Props = OwnProps;

interface ContactUsStyles {
    headerClickable?: HeaderClickableStyles;
    xngTooltip?: FloatingTooltipStyles;
}

export const contactUsStyles: ContactUsStyles = {};

const styles = contactUsStyles;
const ContactUs: FC<Props> = ({ fields }) => {
    const { contactUsUrl, isSticky } = useAppSelector(
        state => ({
            contactUsUrl: getSettingsCollection(state).customSharedSettings.contactUsUrl,
            isSticky: (state as CustomAppState).xHeader.isSticky,
        }),
        shallowEqual,
    );

    const [isMenuOpen, setMenuOpen] = useState(false);

    return (
        <FloatingMenuTooltip
            offset={isSticky ? DEFAULT_STEINER_STICKY_OFFSET : DEFAULT_STEINER_OFFSET}
            onOpenChange={open => setMenuOpen(open)}
            triggerComponent={
                <HeaderClickable
                    isOpen={isMenuOpen}
                    href={contactUsUrl}
                    data-test-selector="ContactUs"
                    aria-label={translate("Contact Us")}
                    isCompactHeader={fields.isCompactHeader}
                    extendedStyles={styles.headerClickable}
                >
                    {translate("Contact Us")}
                </HeaderClickable>
            }
            extendedStyles={styles.xngTooltip}
        >
            <MiniContactUs />
        </FloatingMenuTooltip>
    );
};

const widgetModule: WidgetModule = {
    component: ContactUs,
    definition: {
        group: XNGAGE_ELEMENTS,
        icon: "headset",
        fieldDefinitions: [
            {
                name: fields.isCompactHeader,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isVisible: item => false,
            },
        ],
    },
};

export default widgetModule;
